import React, { useState } from 'react';
import classNames from 'classnames';
import Icon from '../../elements/Icon/Icon';

interface DiscountAllocation {
  amount: string;
  discount_application: {
    title: string;
    type: string;
  };
}

interface CartItemProps {
  settings: {
    cart_item?: {
      key: string;
      id: number;
      quantity: number;
      url: string;
      title: string;
      image: {
        src: string;
        alt: string;
      };
      price: number;
      original_price: number;
      line_price: number;
      final_line_price: number;
      line_level_discount_allocations: DiscountAllocation[];
      line_level_total_discount: number;
      product: {
        title: string;
        handle: string;
        has_only_default_variant: boolean;
      };
      options_with_values: {
        name: string;
        value: string;
      }[];
    };
    // Block settings from schema
    border_enabled: boolean;
    border_color: string;
    border_radius: number;
    color_scheme: string;
    classes: string;
    
    // Image settings
    image_width: number;
    image_shape: 'square' | 'rectangle';
    image_border_radius: number;
    
    // Typography
    title_class: string;
    variant_class: string;
    price_class: string;
    total_class: string;
    total_label: string;
    
    // Quantity controls
    quantity_border_color: string;
    quantity_bg_color: string;
    quantity_text_color: string;
    quantity_text_class: string;
    quantity_border_radius: number;
    quantity_button_color: string;
    quantity_button_hover_bg: string;
    
    // Discount settings
    show_discounts: boolean;
    discount_badge_bg: string;
    discount_badge_text: string;
    discount_class: string;
    discount_message_class: string;
    discount_message_text: string;
    
    // Remove button
    show_remove_button: boolean;
    remove_button_style: 'icon' | 'text';
    remove_button_position: 'corner' | 'side';
    remove_button_text: string;
    remove_button_bg: string;
    remove_button_color: string;
    remove_button_hover_bg: string;
    
    // Animation settings
    enable_animations: boolean;
    animation_type: string;
    animation_duration: number;
    animation_delay: number;
    render_type: string;
  };
}

export const CartItem: React.FC<CartItemProps> = ({ settings }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(settings.cart_item?.quantity || 1);
  const item = settings.cart_item;
  
  if (!item) {
    return (
      <div className="p-4 bg-gray-100 rounded text-center">
        Cart item data not available
      </div>
    );
  }
  
  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
      // In a real implementation, this would update the cart
      // via the cart API
    }
  };
  
  const handleIncreaseQuantity = () => {
    setQuantity(quantity + 1);
    // In a real implementation, this would update the cart
    // via the cart API
  };
  
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(e.target.value);
    if (!isNaN(newQuantity) && newQuantity > 0) {
      setQuantity(newQuantity);
      // In a real implementation, this would update the cart
      // via the cart API
    }
  };
  
  const handleRemoveItem = () => {
    setIsLoading(true);
    // In a real implementation, this would remove the item
    // via the cart API
  };
  
  // Format money function
  const formatMoney = (cents: number) => {
    if (typeof cents === 'string') {
      cents = parseInt(cents);
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };
  
  // Discount message
  const discountMessage = settings.discount_message_text.replace(
    '[amount]',
    formatMoney(item.line_level_total_discount || 0)
  );
  
  return (
    <div 
      className={classNames(
        'cart-item-react',
        { 'opacity-50': isLoading },
        settings.classes
      )}
      data-testid="cart-item-block"
    >
      {/* React implementation of cart item component that enhances the Liquid version */}
      
      {/* This component can add additional functionality that's not easily done in Liquid */}
      
      {/* If enhanced functionality is needed beyond the liquid template, implement it here */}
      
      {/* For example, add animations when items update, visual indicators, etc. */}
      {settings.enable_animations && (
        <div className="cart-item-animation" style={{
          animationName: settings.animation_type,
          animationDuration: `${settings.animation_duration}ms`,
          animationDelay: `${settings.animation_delay}ms`,
          animationFillMode: 'both',
          animationTimingFunction: 'ease-out'
        }}></div>
      )}
    </div>
  );
};

export default CartItem;
